import React, {ReactNode} from 'react';
import {Field as FormikField, FieldAttributes, useField} from "formik";
import {concatenate} from "services/string";
import FieldHeader from "components/Form/FieldHeader";
import FieldHelp from "../Form/Field/FieldHelp";
import {LabelColor} from "../Label/Label";

type Props = {
    label?: string
    icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    hint?: string
    addon?: ReactNode
    labelColor?: LabelColor
}

function Field(props: FieldAttributes<any> & Props) {
    const [, meta] = useField(props);

    let classes: string;

    switch (props.type) {
        case 'checkbox':
            classes = concatenate(props.disabled ? 'bg-gray-400' : 'focus:outline-0 focus:ring-0 focus-visible:ring-2 focus-visible:ring-brand text-brand bg-white', 'h-4 w-4 border rounded border-gray-300');
            break;
        case 'color':
            classes = 'appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer';
            break;
        default:
            classes = 'block w-full text-sm border-none bg-transparent focus:ring-0';
    }

    const isCheckbox = props.type === 'checkbox'
    const isColorPicker = props.type === 'color';
    const isInput = (props.as === undefined || props.as === 'input') && !isCheckbox && !isColorPicker;
    const isSelect = props.as === 'select';
    const isTextArea = props.as === 'textarea';
    const hideIcon = isColorPicker || isCheckbox || isTextArea;
    const hideAddon = isColorPicker || isCheckbox || isTextArea || isSelect;
    const error = (meta.touched && !!meta.error) ? meta.error : null;

    const {label, icon, hint, addon, labelColor, ...fieldProps} = props;

    return (
        <div>
            <div className='flex flex-col gap-1'>
                {(label || hint) &&
                    <FieldHeader fieldId={props.id} label={label} hint={hint} labelColor={labelColor} />
                }

                <div className={(isInput || isSelect || isTextArea) ? 'relative flex rounded-md shadow-sm bg-white border border-gray-300 focus-within:ring-2 focus-within:ring-brand' : ''}>
                    {(!hideIcon && icon) &&
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <props.icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </div>
                    }

                    <FormikField
                        className={concatenate(
                            classes,
                            (!hideIcon && !!icon) && 'pl-10',
                            '',
                            fieldProps.disabled && 'text-gray-500 ring-gray-300 placeholder:text-gray-400 cursor-not-allowed'
                        )}
                        {...fieldProps}
                    />

                    {(!hideAddon && addon) &&
                        <div className="relative inset-y-0 right-0 pl-1 pr-3 flex items-center">
                            <span className="text-gray-500 text-sm">
                                {addon}
                            </span>
                        </div>
                    }
                </div>
            </div>

            {/* Error message */}
            {/* TODO: Remove typeof check until all form validation work has been completed */}
            {(!!error && typeof error === 'string') &&
                <FieldHelp isError={!!error}>
                    {error}
                </FieldHelp>
            }
        </div>
    )
}

export default Field;